import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { ThemeToggle } from "@/components/theme-toggle";
import { Menu, Search, User, Shield, ShoppingBag } from "lucide-react";
import { useState, useEffect, useContext } from "react";
import { AdminAuthContext } from "../App";

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { setIsAdmin: setGlobalIsAdmin } = useContext(AdminAuthContext);

  useEffect(() => {
    fetch("/backend/admin_auth.php", { credentials: "include" })
      .then(res => {
        if (!res.ok) throw new Error('Not authenticated');
        return res.json();
      })
      .then(data => {
        setIsAdmin(data.authenticated);
        setGlobalIsAdmin(data.authenticated);
      })
      .catch(() => {
        setIsAdmin(false);
        setGlobalIsAdmin(false);
      });
  }, [setGlobalIsAdmin]);

  const handleAdminIconClick = () => {
    if (isAdmin) {
      navigate("/admin");
    } else {
      navigate("/admin-login");
    }
  };

  const handleLogout = async () => {
    try {
      await fetch("/backend/admin_auth.php", { 
        method: "DELETE", 
        credentials: "include" 
      });
      setIsAdmin(false);
      setGlobalIsAdmin(false);
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const isActive = (path: string) => location.pathname === path;

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/blog", label: "Reviews" },
    { href: "/about", label: "About" },
    // Removed Admin tab
  ];

  const NavItems = ({ mobile = false }: { mobile?: boolean }) => (
    <>
      {navLinks.map((link) => (
        <Link
          key={link.href}
          to={link.href}
          className={`${
            mobile ? "block px-4 py-2" : "px-4 py-2"
          } transition-colors hover:text-primary ${
            isActive(link.href) 
              ? "text-primary font-semibold border-b-2 border-primary" 
              : "text-foreground"
          }`}
          onClick={() => mobile && setIsOpen(false)}
        >
          {link.label}
        </Link>
      ))}
    </>
  );

  return (
    <nav className="sticky top-0 z-50 w-full bg-background/80 backdrop-blur-md border-b border-border">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <ShoppingBag className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="font-bold text-xl tracking-tight">Shophentic</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <NavItems />
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center space-x-4">
            <Button variant="ghost" size="sm">
              <Search className="h-4 w-4" />
            </Button>
            <ThemeToggle />
            {/* Admin Icon */}
            <Button variant="ghost" size="sm" onClick={handleAdminIconClick} title={isAdmin ? "Go to Admin Dashboard" : "Admin Login"}>
              <Shield className="h-4 w-4" />
            </Button>
            {isAdmin && (
              <Button variant="ghost" size="sm" onClick={handleLogout} title="Logout">
                <User className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Mobile Menu */}
          <div className="md:hidden">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                <div className="flex flex-col space-y-4 mt-8">
                  <NavItems mobile />
                  <div className="border-t pt-4 space-y-2">
                    <Button variant="ghost" className="w-full justify-start">
                      <Search className="h-4 w-4 mr-2" />
                      Search
                    </Button>
                    <div className="flex items-center justify-between px-4 py-2">
                      <span className="text-sm text-muted-foreground">Dark Mode</span>
                      <ThemeToggle />
                    </div>
                    {/* Admin Icon for Mobile */}
                    <Button variant="ghost" className="w-full justify-start" onClick={handleAdminIconClick} title={isAdmin ? "Go to Admin Dashboard" : "Admin Login"}>
                      <Shield className="h-4 w-4 mr-2" />
                      {isAdmin ? "Admin Dashboard" : "Admin Login"}
                    </Button>
                    {isAdmin && (
                      <Button variant="ghost" className="w-full justify-start" onClick={handleLogout} title="Logout">
                        <User className="h-4 w-4 mr-2" />
                        Logout
                      </Button>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};